import { JobPostingStatus } from '~/domain/PermanentJobs';
import { ProfessionTypeEnum } from '~/models';
import { TNullable } from '~/types/Nullable';

/**
 * Resolves the short profession code shown on a job-posting card's role badge
 * (e.g. "RDH" / "DA" / "EFDA") — never the full profession name. The enum values
 * already are the short codes, so the sub-profession wins when present, otherwise
 * the profession. Shared by {@link JobCard} and {@link DraftJobCard} so the two
 * cards render the badge identically (GOTU-5705).
 */
export const getJobRoleBadgeCode = (
  professionType: TNullable<ProfessionTypeEnum>,
  subProfession: TNullable<string>,
): string | undefined => subProfession || professionType || undefined;

interface IStatusDateInput {
  publishedAt?: TNullable<string>;
  closedAt?: TNullable<string>;
  lastSavedAt?: TNullable<string>;
}

/**
 * Builds the timestamp label rendered beside the DS `JobListingCard` status
 * label. The DS already draws the status word ("Active" / "Closed" / "Draft"),
 * so this MUST NOT repeat it — otherwise the row reads "Closed · Closed …" /
 * "Draft · Draft saved …" (GOTU-5706 / GOTU-4690). Closed shows the bare closed
 * date, drafts read "Saved …", everything else reads "Posted …". Centralized so
 * both cards stay in lockstep and future consumers can't reintroduce the repeat.
 */
export const getStatusDateLabel = (
  status: JobPostingStatus,
  { publishedAt, closedAt, lastSavedAt }: IStatusDateInput,
): string => {
  switch (status) {
    case JobPostingStatus.Draft:
      return `Saved ${lastSavedAt}`;
    case JobPostingStatus.Closed:
      return closedAt ?? '';
    default:
      return `Posted ${publishedAt}`;
  }
};
